import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import IRMA from "@models/IRMA";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import RmaService from "@services/RmaService";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import UiContext from "@contexts/UiContext";
import "./ProcessRmaModal.scss";
import NotificationLevelEnum from "@models/NotificationLevelEnum";

type Props = {
    rma: IRMA;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
};

export const ProcessRmaModal = (props: Props) => {
    const { rma, isOpen, onClose, onSuccess } = props;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    const [loading, setLoading] = useState<boolean>(false);

    const calculateSummary = () => {
        const lineItems = rma.RMA_Line_Items || [];

        let totalReceived = 0;
        let totalInspected = 0;
        let totalRefund = 0;
        let totalRestockable = 0;
        const warehouseItems: { [key: string]: number } = {};
        const nonWarehouseItems: { [key: string]: number } = {};
        const conditionBreakdown: { [key: string]: { quantity: number; restockable: number } } = {};
        const validationIssues: string[] = [];

        lineItems.forEach((item) => {
            totalReceived += Number(item.qty_received || 0);

            const inspections = item.RMA_Line_Item_Inspections || [];
            const inspectedQty = inspections.reduce((sum, insp) => sum + Number(insp.quantity), 0);
            totalInspected += inspectedQty;

            // Validation: Check if inspected matches received
            if (inspectedQty !== item.qty_received) {
                validationIssues.push(`${item.sku}: Inspected (${inspectedQty}) does not match received (${item.qty_received})`);
            }

            inspections.forEach((insp) => {
                totalRefund += Number(insp.refund_amount || 0);

                const conditionName = insp.Condition_On_Receipt_Type?.name || "Unknown";
                if (!conditionBreakdown[conditionName]) {
                    conditionBreakdown[conditionName] = { quantity: 0, restockable: 0 };
                }
                conditionBreakdown[conditionName].quantity += Number(insp.quantity);

                if (insp.restockable) {
                    totalRestockable += Number(insp.quantity);
                    conditionBreakdown[conditionName].restockable += Number(insp.quantity);

                    // Validation: Check warehouse assigned
                    if (supplierModel.has_warehouses) {
                        if (!insp.restock_warehouse_id) {
                            validationIssues.push(`${item.sku}: Restockable inspection missing warehouse assignment`);
                        } else {
                            const warehouseName = insp.restock_warehouse?.name || `Warehouse ${insp.restock_warehouse_id}`;
                            warehouseItems[warehouseName] = (warehouseItems[warehouseName] || 0) + Number(insp.quantity);
                        }
                    } else {
                        if (insp.restockable) {
                            nonWarehouseItems[item.sku] = (nonWarehouseItems[item.sku] || 0) + Number(insp.quantity);
                        }
                    }
                }
            });
        });

        // Check if all items are fully received
        const itemsNotFullyReceived = lineItems.filter((item) => (item.qty_received || 0) < item.qty_returned);
        if (itemsNotFullyReceived.length > 0) {
            itemsNotFullyReceived.forEach((item) => {
                const received = item.qty_received || 0;
                const remaining = item.qty_returned - received;
                if (received === 0) {
                    validationIssues.push(`${item.sku}: No items received yet (${item.qty_returned} expected)`);
                } else {
                    validationIssues.push(`${item.sku}: Only ${received}/${item.qty_returned} received (${remaining} remaining)`);
                }
            });
        }

        return {
            totalReceived,
            totalInspected,
            totalRefund,
            totalRestockable,
            warehouseItems,
            nonWarehouseItems,
            conditionBreakdown,
            validationIssues,
            canProcess: validationIssues.length === 0,
        };
    };

    const summary = calculateSummary();

    const handleProcess = async () => {
        if (!summary.canProcess) {
            uiContext.showNotification({
                message: "Please resolve all validation issues before processing",
                level: NotificationLevelEnum.error,
            });
            return;
        }

        try {
            setLoading(true);
            await RmaService.processRma(rma.guid, userModel, supplierModel);

            uiContext.showNotification({
                message: "RMA processed successfully",
                level: NotificationLevelEnum.success,
            });
            onSuccess();
            onClose();
        } catch (err) {
            uiContext.showNotification({
                message: err instanceof Error ? err.message : "Failed to process RMA",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={isOpen} onHide={onClose} size="lg" className="ProcessRmaModal" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <OwLabel title="process_rma" /> - {rma.rma_number}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h5>
                    <OwLabel title="review_summary_before_processing" />
                </h5>

                {/* Validation Checks */}
                <div className="mt-3">
                    <h6>
                        <OwLabel title="pre_processing_checks" />
                    </h6>
                    {summary.validationIssues.length === 0 ? (
                        <>
                            <div className="text-success">
                                <i className="fa fa-circle-check" /> <OwLabel title="all_line_items_received" />
                            </div>
                            <div className="text-success">
                                <i className="fa fa-circle-check" /> <OwLabel title="all_received_items_inspected" />
                            </div>
                            {supplierModel.has_warehouses && (
                                <div className="text-success">
                                    <i className="fa fa-circle-check" /> <OwLabel title="all_restockable_items_have_warehouse" />
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="alert alert-danger">
                            <strong>
                                <OwLabel title="validation_issues" />:
                            </strong>
                            <ul className="mb-0 mt-2">
                                {summary.validationIssues.map((issue, index) => (
                                    <li key={index}>{issue}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Inspection Summary */}
                <div className="mt-4">
                    <h6>
                        <OwLabel title="inspection_summary" />
                    </h6>
                    <p>
                        <OwLabel title="total_received" />: <strong>{summary.totalReceived}</strong> items
                    </p>
                    <p>
                        <OwLabel title="total_inspected" />: <strong>{summary.totalInspected}</strong> items
                    </p>

                    <h6 className="mt-3">
                        <OwLabel title="condition_breakdown" />:
                    </h6>
                    {Object.entries(summary.conditionBreakdown).map(([condition, data]) => (
                        <div key={condition}>
                            • {condition}: <strong>{data.quantity}</strong> items ({data.restockable} restockable)
                        </div>
                    ))}
                </div>

                {/* Inventory Changes */}
                {Object.keys(summary.warehouseItems).length > 0 && (
                    <div className="mt-4">
                        <h6>
                            <OwLabel title="inventory_changes" />
                        </h6>
                        {Object.entries(summary.warehouseItems).map(([warehouse, quantity]) => (
                            <div key={warehouse}>
                                {warehouse}: <strong>+{quantity}</strong> items
                            </div>
                        ))}
                    </div>
                )}

                {Object.keys(summary.nonWarehouseItems).length > 0 && (
                    <div className="mt-4">
                        <h6>
                            <OwLabel title="items_restocked" />
                        </h6>
                        {Object.entries(summary.nonWarehouseItems).map(([sku, quantity]) => (
                            <div key={sku}>
                                {sku}: <strong>+{quantity}</strong> items
                            </div>
                        ))}
                    </div>
                )}

                {/* Refunds */}
                <div className="mt-4">
                    <h6>
                        <OwLabel title="refunds" />
                    </h6>
                    <p>
                        <OwLabel title="total_refund_amount" />: <strong>${summary.totalRefund.toFixed(2)}</strong>
                    </p>
                </div>

                {/* Warning */}
                <div className="alert alert-warning mt-4">
                    <strong>
                        ⚠️ <OwLabel title="warning" />:
                    </strong>{" "}
                    <OwLabel title="process_rma_warning" />
                    <ul className="mb-0 mt-2">
                        <li>{summary.totalRestockable} items will be added back to inventory</li>
                        <li>Status will change to &quot;Processed&quot;</li>
                        <li>Inspections can no longer be edited</li>
                        <li>RMA cannot be cancelled</li>
                    </ul>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <OwButton title="cancel" fn={onClose} active={loading} className="btn-secondary me-2" />
                <OwButton
                    title="confirm_process"
                    className="btn-success me-2"
                    fn={handleProcess}
                    active={loading}
                    disabled={!summary.canProcess}
                />
            </Modal.Footer>
        </Modal>
    );
};
